import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import AppTable from '../../../components/AppTable/AppTable';
import { setPagination } from '../../../redux/Actions/TableAction';
import { searchStockTransaction, acceptStockTransaction, rejectStockTransaction } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';
import TableSearch from '../../../components/TableSearch/TableSearch';

function StockReceiving({ isModal = false, onClose, transferFrom = null, productType = null }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Use local state for modal to avoid conflicts with warehouse data
    const [localReceivingData, setLocalReceivingData] = useState([]);
    const [localPagination, setLocalPagination] = useState({
        total: 0,
        page: 1,
        limit: 10
    });

    const receivingData = isModal ? localReceivingData : (Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []);

    // Local state for loading and error handling
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Bulk selection state
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Permission checks
    const canReadReceiving = true;
    const canMarkReceived = true;

    // Initialize with empty array to prevent map errors
    useEffect(() => {
        if (!isModal && !Array.isArray(ApiReducer.apiJson)) {
            dispatch(setApiJson([]));
        }
    }, []);

    // Memoized function to search pending stock receiving
    const searchStockReceiving = useCallback(async (searchKey, searchValue) => {
        setIsLoading(true);
        setIsSearching(true);
        setError(null);

        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                status: 'transferd' // Filter for transferred receiving only
            };

            // Add transferFrom filter based on context
            if (transferFrom) {
                searchObj.transferFrom = transferFrom;
            }

            // Add productType filter based on context
            if (productType) {
                searchObj.type = productType;
            }

            // Only add search key/value if both are provided
            if (searchKey && searchValue) {
                // Handle product-related searches with population
                const productFields = ['productName', 'brand', 'productType'];
                const transactionFields = ['status', 'place', 'stockLocation', 'transferFrom', 'transactionReason'];
                
                if (productFields.includes(searchKey)) {
                    // For product fields, search in populated productId field
                    searchObj[`productId.${searchKey}`] = {
                        $regex: searchValue,
                        $options: 'i'
                    };
                } else if (transactionFields.includes(searchKey)) {
                    // Handle text transaction fields
                    searchObj[searchKey] = {
                        $regex: searchValue,
                        $options: 'i'
                    };
                } else if (searchKey === 'transferQuantity' || searchKey === 'stockIn') {
                    // Handle numeric fields
                    searchObj[searchKey] = parseFloat(searchValue) || 0;
                }
            }

            const currentPagination = isModal ? localPagination : TableDataReducer.pagination;
            
            const json = {
                page: currentPagination.page,
                limit: currentPagination.limit,
                search: searchObj,
                populate: ['productId'] // Ensure product data is populated
            };

            const res = await HitApi(json, searchStockTransaction);

            // Handle response
            let receivingTransactions = [];
            let totalCount = 0;

            if (res?.statusCode === 200) {
                // Transaction API response structure
                receivingTransactions = res?.data?.docs || res?.transactions || res?.result || [];
                totalCount = res?.data?.totalDocs || res?.total || 0;

                // Ensure we have an array
                if (!Array.isArray(receivingTransactions)) {
                    console.warn("Stock Transaction API returned non-array data:", receivingTransactions);
                    receivingTransactions = [];
                }

                // Transform transaction data for receiving display
                const receivingDisplayData = receivingTransactions.map(transaction => ({
                    _id: transaction._id,
                    productId: transaction.productId?._id || transaction.productId,
                    productName: transaction.productId?.productName || 'Unknown Product',
                    brand: transaction.productId?.brand || '',
                    productType: transaction.productId?.productType || '',
                    productImageUrl: transaction.productId?.productImageUrl || '',
                    transferQuantity: transaction.transferQuantity || 0,
                    stockIn: transaction.stockIn || 0,
                    stockOut: transaction.stockOut || 0,
                    status: transaction.status || 'pending',
                    place: transaction.place || 'warehouse',
                    stockLocation: transaction.stockLocation || 'warehouse',
                    transferFrom: transaction.transferFrom || '',
                    transactionReason: transaction.transactionReason || '',
                    transferNotes: transaction.transferNotes || '',
                    type: transaction.type || 'professional',
                    createdAt: transaction.createdAt,
                    updatedAt: transaction.updatedAt
                }));

                if (isModal) {
                    // Use local state for modal
                    setLocalReceivingData(receivingDisplayData);
                    setLocalPagination({
                        total: totalCount,
                        page: currentPagination.page,
                        limit: currentPagination.limit
                    });
                } else {
                    // Use Redux for standalone page
                    dispatch(setApiJson(receivingDisplayData));
                    
                    // Update pagination data in Redux
                    const paginationData = {
                        total: totalCount,
                        page: TableDataReducer.pagination.page,
                        limit: TableDataReducer.pagination.limit
                    };
                    
                    dispatch(setPagination(paginationData));
                }
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to fetch pending stock receiving';
                console.error("Stock Receiving API Error:", errorMessage);
                setError(errorMessage);
                
                if (isModal) {
                    setLocalReceivingData([]);
                } else {
                    dispatch(setApiJson([]));
                }
                
                toast.error('Failed to fetch pending stock receiving. Please try again.');
            }
        } catch (error) {
            console.error("Error fetching stock receiving:", error);
            setError('Failed to fetch stock receiving. Please check your connection and try again.');
            
            if (isModal) {
                setLocalReceivingData([]);
            } else {
                dispatch(setApiJson([]));
            }
            
            toast.error('Error fetching stock receiving. Please try again.');
        } finally {
            setIsLoading(false);
            setIsSearching(false);
        }
    }, [dispatch, TableDataReducer.pagination.page, TableDataReducer.pagination.limit, isModal, localPagination.page, localPagination.limit]);

    useEffect(() => {
        if (canReadReceiving) {
            searchStockReceiving();
        }
    }, [searchStockReceiving, canReadReceiving]);

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }

        let searchValue = valueSearched.trim();

        // Convert numeric fields to numbers
        const numericFields = ['transferQuantity', 'stockIn'];
        if (numericFields.includes(optionSelect)) {
            const numericValue = parseFloat(searchValue);
            if (isNaN(numericValue)) {
                toast.error('Please enter a valid number for numeric fields');
                return;
            }
            searchValue = numericValue;
        }

        searchStockReceiving(optionSelect, searchValue);
    };

    // Search options for stock receiving
    const options = [
        { label: 'Product Name', value: 'productName' },
        { label: 'Brand', value: 'brand' },
        { label: 'Product Type', value: 'productType' },
        { label: 'Transfer Quantity', value: 'transferQuantity' },
        { label: 'Stock In', value: 'stockIn' },
        { label: 'Transfer From', value: 'transferFrom' },
        { label: 'Transfer Reason', value: 'transactionReason' },
    ];

    // Format date
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    // Accept stock functionality
    const handleAcceptStock = async (receivingId, productName) => {
        if (!receivingId) {
            toast.error('Receiving ID is required');
            return;
        }

        const confirmAccept = window.confirm(
            `Accept stock for "${productName}"? This will update the stock status to received.`
        );

        if (!confirmAccept) return;

        setIsLoading(true);
        
        try {
            const json = {
                _id: receivingId,
                receivingFrom: transferFrom || 'warehouse' // Pass the receivingFrom parameter
            };

            const res = await HitApi(json, acceptStockTransaction);
            
            if (res?.statusCode === 200) {
                toast.success('Stock accepted successfully');
                searchStockReceiving();
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to accept stock';
                console.error("Accept Stock API Error:", errorMessage);
                toast.error(errorMessage);
            }
            
        } catch (error) {
            console.error("Error accepting stock:", error);
            toast.error('Error accepting stock. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Reject stock functionality
    const handleRejectStock = async (receivingId, productName) => {
        if (!receivingId) {
            toast.error('Receiving ID is required');
            return;
        }

        const confirmReject = window.confirm(
            `Reject stock for "${productName}"? This will mark the transfer as rejected.`
        );

        if (!confirmReject) return;

        setIsLoading(true);
        
        try {
            const json = {
                _id: receivingId
            };

            const res = await HitApi(json, rejectStockTransaction);
            
            if (res?.statusCode === 200) {
                toast.success('Stock rejected successfully');
                searchStockReceiving();
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to reject stock';
                console.error("Reject Stock API Error:", errorMessage);
                toast.error(errorMessage);
            }
            
        } catch (error) {
            console.error("Error rejecting stock:", error);
            toast.error('Error rejecting stock. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Bulk accept functionality
    const handleBulkAccept = async () => {
        if (selectedItems.length === 0) {
            toast.error('Please select items to accept');
            return;
        }

        const confirmBulkAccept = window.confirm(
            `Accept ${selectedItems.length} selected items? This will mark them as received.`
        );

        if (!confirmBulkAccept) return;

        setIsLoading(true);
        
        try {
            // Call acceptStockTransaction for each selected item
            const promises = selectedItems.map(itemId => {
                const json = {
                    _id: itemId,
                    receivingFrom: transferFrom || 'warehouse' // Pass the receivingFrom parameter
                };
                return HitApi(json, acceptStockTransaction);
            });

            const results = await Promise.all(promises);
            
            // Check if all requests were successful
            const successfulCount = results.filter(res => res?.statusCode === 200).length;
            const failedCount = results.length - successfulCount;
            
            if (failedCount === 0) {
                toast.success(`${selectedItems.length} items accepted successfully`);
            } else if (successfulCount > 0) {
                toast.success(`${successfulCount} items accepted successfully. ${failedCount} failed.`);
            } else {
                toast.error('Failed to accept selected items. Please try again.');
            }
            
            setSelectedItems([]);
            setSelectAll(false);
            searchStockReceiving();
            
        } catch (error) {
            console.error("Error bulk accepting stock:", error);
            toast.error('Error accepting selected items. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Bulk reject functionality
    const handleBulkReject = async () => {
        if (selectedItems.length === 0) {
            toast.error('Please select items to reject');
            return;
        }

        const confirmBulkReject = window.confirm(
            `Reject ${selectedItems.length} selected items? This will mark them as rejected.`
        );

        if (!confirmBulkReject) return;

        setIsLoading(true);
        
        try {
            // Call rejectStockTransaction for each selected item
            const promises = selectedItems.map(itemId => {
                const json = {
                    _id: itemId
                };
                return HitApi(json, rejectStockTransaction);
            });

            const results = await Promise.all(promises);
            
            // Check if all requests were successful
            const successfulCount = results.filter(res => res?.statusCode === 200).length;
            const failedCount = results.length - successfulCount;
            
            if (failedCount === 0) {
                toast.success(`${selectedItems.length} items rejected successfully`);
            } else if (successfulCount > 0) {
                toast.success(`${successfulCount} items rejected successfully. ${failedCount} failed.`);
            } else {
                toast.error('Failed to reject selected items. Please try again.');
            }
            
            setSelectedItems([]);
            setSelectAll(false);
            searchStockReceiving();
            
        } catch (error) {
            console.error("Error bulk rejecting stock:", error);
            toast.error('Error rejecting selected items. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const currentPaginationForIndex = isModal ? localPagination : TableDataReducer.pagination;
    const baseIndex = (currentPaginationForIndex.page - 1) * currentPaginationForIndex.limit;

    // Handle bulk selection
    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);
        
        if (checked) {
            // Select only transferd items
            const transferdItems = receivingData
                .filter(item => item.status === 'transferd')
                .map(item => item._id);
            setSelectedItems(transferdItems);
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    // Update selectAll when individual items are selected/deselected
    useEffect(() => {
        const transferdItems = receivingData.filter(item => item.status === 'transferd');
        const selectedTransferdItems = selectedItems.filter(id => 
            transferdItems.some(item => item._id === id)
        );
        
        setSelectAll(transferdItems.length > 0 && selectedTransferdItems.length === transferdItems.length);
    }, [selectedItems, receivingData]);

    // Define stock receiving table headers
    const tableHeaders = [
        {
            title: (
                <div className="flex items-center justify-center">
                    <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={isLoading}
                    />
                </div>
            ),
            key: "select",
            width: "50px",
            align: "center",
            priority: 1,
            render: (_, row) => (
                <div className="flex items-center justify-center">
                    <input
                        type="checkbox"
                        checked={selectedItems.includes(row._id)}
                        onChange={() => handleSelectItem(row._id)}
                        disabled={row.status !== 'transferd' || isLoading}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                </div>
            )
        },
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "start",
            priority: 1,
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Product",
            key: "productName",
            priority: 1,
            render: (value, row) => (
                <div className="flex items-start">
                    {row.productImageUrl ? (
                        <img
                            src={row.productImageUrl}
                            alt={value || 'Product'}
                            className="w-10 h-10 rounded-lg mr-3 object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div className="w-10 h-10 rounded-lg bg-green-200 flex items-center justify-center mr-3 flex-shrink-0"
                        style={{ display: row.productImageUrl ? 'none' : 'flex' }}>
                        <span className="text-lg">📦</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate" title={value}>
                            {value || 'Unnamed Product'}
                        </div>
                        {row.brand && (
                            <div className="text-sm text-gray-500 truncate mt-1" title={row.brand}>
                                {row.brand}
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: "Quantity Expected",
            key: "transferQuantity",
            align: "center",
            priority: 1,
            render: (value) => (
                <span className="font-medium px-2 py-1 rounded text-sm bg-blue-100 text-blue-800">
                    {value || 0}
                </span>
            )
        },
        {
            title: "Transfer Details",
            key: "transferDetails",
            align: "start",
            priority: 2,
            render: (_, row) => (
                <div className="text-sm">
                    <div className="flex items-center mb-1">
                        <span className="text-gray-500">From:</span>
                        <span className="ml-1 font-medium capitalize">
                            {row.transferFrom || 'Unknown'}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <span className="text-gray-500">To:</span>
                        <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                            row.stockLocation === 'warehouse' 
                                ? 'bg-blue-100 text-blue-800' 
                                : row.stockLocation === 'salon'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-purple-100 text-purple-800'
                        }`}>
                            {row.stockLocation === 'warehouse' ? 'Warehouse' : 
                             row.stockLocation === 'salon' ? 'Salon' : 
                             row.stockLocation === 'brand' ? 'Brand' : row.stockLocation}
                        </span>
                    </div>
                </div>
            )
        },
        {
            title: "Status",
            key: "status",
            align: "center",
            priority: 2,
            render: (value) => (
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    value === 'pending' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : value === 'received'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                    {value === 'pending' ? 'Pending' : 
                     value === 'received' ? 'Received' : value}
                </span>
            )
        },
        {
            title: "Transfer Date",
            key: "createdAt",
            priority: 3,
            render: (value) => (
                <span className="text-sm text-gray-600">
                    {formatDate(value)}
                </span>
            )
        },
        {
            title: "Reason",
            key: "transactionReason",
            priority: 4,
            render: (value) => (
                <span className="text-sm text-gray-600" title={value}>
                    {value || 'No reason specified'}
                </span>
            )
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            priority: 1,
            render: (_, row) => (
                <div className="flex justify-center space-x-2">
                    {canMarkReceived && row.status === 'transferd' && (
                        <>
                            <button
                                onClick={() => handleAcceptStock(row._id, row.productName)}
                                disabled={isLoading}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors text-sm disabled:opacity-50"
                                title="Accept stock"
                            >
                                Accept
                            </button>
                            <button
                                onClick={() => handleRejectStock(row._id, row.productName)}
                                disabled={isLoading}
                                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors text-sm disabled:opacity-50"
                                title="Reject stock"
                            >
                                Reject
                            </button>
                        </>
                    )}
                    {row.status === 'received' && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm">
                            Received
                        </span>
                    )}
                    {row.status === 'rejected' && (
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded text-sm">
                            Rejected
                        </span>
                    )}
                </div>
            )
        }
    ];

    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        searchStockReceiving();
    };

    const content = (
        <>
            <PageHeader
                title="Stock Receiving"
                description="View and manage pending stock receiving"
            />

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium">Error Loading Stock Receiving</h4>
                            <p className="mt-1">{error}</p>
                        </div>
                        <button
                            onClick={() => searchStockReceiving()}
                            disabled={isLoading}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {isLoading ? 'Loading...' : 'Retry'}
                        </button>
                    </div>
                </div>
            )}

            {!canReadReceiving ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading permissions...</div>
                </div>
            ) : (
                <>
                    {/* Navigation/Close Button and Bulk Actions */}
                    <div className="mb-4 flex items-center justify-between">
                        <div>
                            {isModal ? (
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                                >
                                    ✕ Close
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate("/warehouse")}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                                >
                                    ← Back to Warehouse
                                </button>
                            )}
                        </div>
                        
                        {/* Bulk Action Buttons */}
                        {selectedItems.length > 0 && (
                            <div className="flex space-x-3">
                                <span className="text-sm text-gray-600 self-center">
                                    {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                                </span>
                                <button
                                    onClick={handleBulkAccept}
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                                >
                                    Accept Selected ({selectedItems.length})
                                </button>
                                <button
                                    onClick={handleBulkReject}
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                                >
                                    Reject Selected ({selectedItems.length})
                                </button>
                            </div>
                        )}
                    </div>

                    <AppTable
                        TH={tableHeaders}
                        TD={receivingData}
                        isLoading={isLoading || isSearching}
                        emptyMessage={error ? "Failed to load stock receiving data. Please try again." : "No pending stock receiving found."}
                        TableSearch={
                            <TableSearch
                                options={options}
                                onClick={handleSearch}
                                optionSelect={optionSelect}
                                setOptionSelect={setOptionSelect}
                                setValueSearched={setValueSearched}
                                valueSearched={valueSearched}
                                onClear={handleClearSearch}
                            />
                        }
                    />
                </>
            )}
        </>
    );

    if (isModal) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-5/6 overflow-hidden">
                    <div className="p-5 h-full overflow-y-auto">
                        {content}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            {content}
        </div>
    );
}

export default StockReceiving;